import React, {useEffect, useMemo, useState} from 'react';
import {notification, Tag, Typography} from 'antd';
import {Navbar} from "../../components/Navbar";
import {authAPI} from "../../services/AuthService";
import {Navigate} from "react-router-dom";
import {taskAPI} from "../../services/TaskService";
import {TaskGridModel} from "../../models/TaskGridModel";
import {TaskModal} from "../../components/TaskScreen/TaskModal";
import {host, port, secure} from "../../config/config";
import {CreateTaskModal} from "../../components/TaskScreen/CreateTaskModal";
import {
    DataGridPremium,
    GridCellParams,
    gridClasses,
    GridColDef,
    GridRenderCellParams,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
    ruRU
} from "@mui/x-data-grid-premium";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {Button} from '@mui/material';

const {Text} = Typography;
const Context = React.createContext({name: 'TasksScreen'});

export const TasksScreen: React.FC = () => {
    const CustomToolbar = () => {
        return (
            <GridToolbarContainer>
                <div style={{display: 'flex', justifyContent: 'space-between', width: window.innerWidth}}>
                    <div style={{display: 'flex'}}>
                        <Button size={'small'} onClick={() => setVisibleCreateTaskModal(true)}
                                startIcon={<AddBoxOutlinedIcon/>}>Добавить</Button>
                        <Button disabled={isGettingTasksInProcess} size={'small'} onClick={() => getTasksRequest()}
                                startIcon={<RefreshOutlinedIcon/>}>Обновить</Button>
                        <GridToolbarColumnsButton/>
                        <GridToolbarFilterButton/>
                        <GridToolbarExport/>
                    </div>
                    <div style={{display: 'flex'}}>
                        <GridToolbarQuickFilter/>
                    </div>
                </div>
            </GridToolbarContainer>
        );
    }
    // Notification
    const [api, contextHolder] = notification.useNotification();
    const errorNotification = (message: string) => {
        api.error({
            message: `Ошибка`,
            description: <Context.Consumer>{({name}) => `${message}`}</Context.Consumer>,
            placement: 'topRight',
        });
    };
    const infoNotification = (message: string) => {
        api.success({
            message: `Выполнено`,
            description: <Context.Consumer>{({name}) => `${message}`}</Context.Consumer>,
            placement: 'topRight',
        });
    };
    const contextValue = useMemo(() => ({name: 'Ant Design'}), []);
    // -----
    const [getTasksRequest, {
        data: dataGetTasksRequest,
        isLoading: isGettingTasksInProcess,
        isError,
    }] = taskAPI.useGetAllTasksMutation();
    const [redirectToLogin, setRedirectToLogin] = useState<boolean>(false);
    const [data, setData] = useState<TaskGridModel[]>([]);
    const [selectedTask, setSelectedTask] = useState<TaskGridModel | null>(null);
    const [visibleTaskModal, setVisibleTaskModal] = useState<boolean>(false);
    const [visibleCreateTaskModal, setVisibleCreateTaskModal] = useState<boolean>(false);
    // Return to login if reject token verify
    const [verifyTokenRequest, {status: statusVerifyTokenRequest}] = authAPI.useVerifyTokenMutation();
    useEffect(() => {
        verifyTokenRequest();
    }, [])
    useEffect(() => {
        if (statusVerifyTokenRequest === 'rejected') {
            setRedirectToLogin(true);
        }
    }, [statusVerifyTokenRequest])
    // -----
    useEffect(() => {
        const token: string | null = localStorage.getItem('token');
        if (token) {
            getTasksRequest();
        } else setRedirectToLogin(true);
    }, [])
    useEffect(() => {
        if (dataGetTasksRequest) setData(dataGetTasksRequest);
    }, [dataGetTasksRequest])
    const columns: GridColDef[] = [
        {fieldName: "Статус", field: 'status'},
        {fieldName: "Дата", field: 'date'},
        {fieldName: "Объект", field: 'filialAndObject'},
        {fieldName: "Оборудование", field: 'equipment'},
        {fieldName: "Заказчик", field: 'creator'},
        {fieldName: "Заявленная неисправность", field: 'title'},
        {fieldName: "Описание неисправности", field: 'description'},
    ].map((col: { fieldName: string, field: string }) => {
        return {
            field: col.field,
            headerName: col.fieldName,
            minWidth: col.field === 'status' ? 130 :
                col.field === 'date' ? 200 :
                    250,
            width: col.field === 'status' ? 130 :
                col.field === 'date' ? 200 :
                    250,
            editable: false,
            type: col.field === 'date' ? 'dateTime' : 'string',
            valueGetter: (params) => {
                if (params.value === undefined)
                    return params.value;
                if (col.field === 'date') {
                    return new Date(params.value)
                }
                return params.value;
            },
            renderCell: (params: GridRenderCellParams<any, any>) => {
                if (col.field === 'status') {
                    return <Tag color={params.value === 'Создано' ? 'volcano' :
                        params.value === 'В работе' ? 'processing' : 'success'}>{params.value}</Tag>
                }
            }
        }
    })
    const [reportIsLoading, setReportIsLoading] = useState(false);

    const exportHandler = () => {
        setReportIsLoading(true)
        try {
            let myHeaders = new Headers();
            myHeaders.append("Authorization", `Bearer ${localStorage.getItem("token")}`);

            let requestOptions = {
                method: 'GET',
                headers: myHeaders,
                redirect: 'follow'
            };

            // @ts-ignore
            fetch(`${secure}://${host}${port}/tasks/allExcel`, requestOptions)
                .then(response => {
                    if (response.ok && (response.status === 200)) {
                        return response.blob();
                    }
                })
                .then(data => {
                    // @ts-ignore
                    const url = window.URL.createObjectURL(data);
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "Заявки.xls");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setReportIsLoading(false)
                });
        } catch (error) {
            console.log(error);
        }
    }

    return (
        <Context.Provider value={contextValue}>
            {contextHolder}
            {visibleCreateTaskModal && <CreateTaskModal
                infoNotification={infoNotification}
                errorNotification={errorNotification}
                getTasks={getTasksRequest}
                setVisible={setVisibleCreateTaskModal}/>}
            <div style={{height: window.innerHeight - 60}}>
                {redirectToLogin && <Navigate to="/login" replace={false}/>}
                {(visibleTaskModal && selectedTask) &&
                    <TaskModal
                        selectedTask={selectedTask}
                        infoNotification={infoNotification}
                        errorNotification={errorNotification}
                        getTasks={getTasksRequest}
                        setVisible={setVisibleTaskModal}
                    />}
                <Navbar/>
                <DataGridPremium
                    localeText={ruRU.components.MuiDataGrid.defaultProps.localeText}
                    rows={data}
                    columns={columns}
                    getRowHeight={() => 'auto'}
                    initialState={{
                        sorting: {
                            sortModel: [{field: 'date', sort: 'desc'}],
                        },
                    }}
                    sx={{
                        [`& .${gridClasses.cell}`]: {
                            py: 1,
                        },
                    }}
                    /*@ts-ignore*/
                    pageSize={20}
                    rowsPerPageOptions={[20]}
                    disableSelectionOnClick
                    slots={{toolbar: CustomToolbar}}
                    loading={isGettingTasksInProcess}
                    onCellDoubleClick={(params: GridCellParams) => {
                        setSelectedTask(params.row);
                        setVisibleTaskModal(true);
                    }}
                />
            </div>
        </Context.Provider>
    );
}